import { Component, EventEmitter, Input, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Subject } from 'rxjs';
import { openDialogGetResult } from 'src/app/helpers/angular-helpers';
import { date2str, jsonCopyObject, listDifference, listUnion } from 'globals/helpers/helpers';
import { deleteFalseEntriesInPlace, loadLocal } from 'src/app/helpers/helpers';
import { taskTableColumns, TaskTableData } from 'src/app/helpers/table-column-types';
import { TableColumn, TableMenuItem } from 'src/app/helpers/table.data';
import { JoiEvent, LabelObject, WFWorker, WorkforceGroup } from 'src/app/store/events.era';
import { toggleFilterTypes, FilterGroupTypes } from 'src/app/store/filters.era';
import { allTaskColumnsReselector, myTaskColumnsReselector, taskColumnsReselector, taskTablesReselectorGenerator } from 'src/app/store/reselectors/tasks.reselector';
import { Task } from 'src/app/store/tasks.era';
import { NestedTableComponent, TableIconEmitterData } from '../nested-table/nested-table.component';
import { allTaskColumnsById } from './../../helpers/table-helpers';
import { ConfirmDeleteData, openConfirmDeleteGetResult } from '../dialog.confirmDelete.component';
import { TASK_SAVE_DEBOUNCE_AND_SKIP_DUPLICATE_MS } from 'src/app/helpers/constants';
import { ChangeScheduleDateDialogComponent } from 'src/app/modules/onsiteschedule/onsite.dialog.changeScheduleDate.component';
import { ButtonListData, ButtonListDialogComponent } from '../dialogues-general/buttonList.dialog.component';
import { getSingleWorkforcePersonEmail, PeoplePickerComponent, PeoplePickerData } from '../people-picker/people-picker.component';
import { takeUntil } from 'rxjs/operators';
import { LabelMakerData, LabelMakerDialogComponent, LabelUpdateFunctionParams } from '../label-marker/label-maker.dialog.component';
import { saveTable } from 'src/app/modules/performers/performerSharedFunctions';
import { Wrapper } from '../wrapper.component';
import { TaskViewService } from 'src/app/services/task.view.service';
import { addLabelsToMany, removeLabelsFromMany } from '../../helpers/label-helpers';
import { Label } from 'globals/store/events.data';
import { PROGRAM_NAMES, Program } from 'globals/store/programs.data';
import { TaskStatusDialogData, TaskStatusDropdownDialogComponent } from '../../modules/tasks/task-status-dropdown.component';
import { TaskStatus } from 'globals/store/tasks.data';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'tasks-table',
    template: `
<nested-table
  [items]="tableData"
  [filtersId]="filterPage"
  [condensedView]="condensed"
  [columns]="columnData"
  [readOnly]="readOnly"
  [columnsById]="columnsById"
  [menuItems]="menuItems"
  [defaultSelectedColumnId]="taskColumns.task"
  [menuDisabled]="menuDisabled"
  (selectItem)="manageTask($event)"
  (iconClick)="toggleIcon($event)"
  (seenBarClick)="toggleSeen($event)"
  (updatedSelectedIds)="selectIds($event)"
  [defaultNestedColumnId]="'title'"
  [emptyMessage]="emptyMessage"
></nested-table>
`,
    styles: [`
  :host { display: flex; height: 100%; overflow: hidden; flex-direction: column; }
`],
    standalone: true,
    providers: [DatePipe],
    imports: [NestedTableComponent],
})

export class TasksTableComponent extends Wrapper<TasksTableComponent> implements OnInit {
  @Input() filterPage: keyof FilterGroupTypes
  @Input() saveType
  @Input() saveName
  @Input() otherChangeDetectorRef: ChangeDetectorRef
  @Input() readOnly = false
  @Input() entityId = null
  @Input() allTasks = false
  @Input() isNestedDialog = false
  @Input() initialized = true

  @ViewChild(NestedTableComponent) nestedTable: NestedTableComponent<TaskTableData>
  taskColumns = taskTableColumns
  columnData: TableColumn[] = []
  columnsById: { [id: string]: TableColumn } = allTaskColumnsById
  tableData: TaskTableData[] = []
  tableRowsById: { [id:string]: TaskTableData } = {}
  singleEventMenuItems: TableMenuItem[] = [
    { title: `Set ${ PROGRAM_NAMES.btn_lower }`, method: this.setProgram.bind(this) },
    { title: `Assign to person`, method: this.assignPerson.bind(this) },
    { title: `Manage visibility`, method: this.manageVisibility.bind(this) },
  ]
  menuItems: TableMenuItem[] = []
  selectedTaskIds: string[]
  emptyMessage: string
  condensed: boolean
  menuDisabled = false
  event: JoiEvent

  workforce: WFWorker[] = []
  peopleEmitter: EventEmitter<any> = new EventEmitter
  wf_groups: WorkforceGroup[] = []
  workersByUId: { [key: string]: WFWorker } = {}
  task_labels: LabelObject = {}

  constructor(public changeDetectorRef: ChangeDetectorRef, private taskViewService: TaskViewService) {
    super()
  }

  ngOnInit() {
    this.subProp(this.store.select('events', 'event'), 'event')
    this.subCallback(this.store.select('filters', 'toggleFilters', this.filterPage, 'condensed'), _ => this.loadCondensed())
    this.selectCallback(taskTablesReselectorGenerator(this.filterPage, this.entityId), null, ((data: any) => {
      if (!data) {
        this.tableData = [] // for when the last is deleted
        return
      }
      const { taskTableData, taskTableRowsById, loadingCount } = data
      this.tableData = taskTableData
      this.tableRowsById = taskTableRowsById
      // console.log(taskTableData)
      // console.log({ loadingCount })
      if (loadingCount) this.emptyMessage = `Fetching ${loadingCount} task subscriptions...`
      else if (!this.initialized) this.emptyMessage = 'Getting accounts...'
      else this.emptyMessage = this.allTasks ? 'No visible tasks in any events' : 'No visible tasks'
      //console.log({ taskTableData, taskTableRowsById })
    }))
    const columnsReselector = this.filterPage === 'allTasks' ? allTaskColumnsReselector : this.filterPage === 'myTasks' ? myTaskColumnsReselector : taskColumnsReselector
    this.selectProp(columnsReselector, null, 'columnData')
    this.subProp(this.store.select('workforce', 'workforce'), 'workforce')
    this.subProp(this.store.select('workforce', 'wf_groups'), 'wf_groups')
    this.subProp(this.store.select('workforce', 'workersByUId'), 'workersByUId')
    this.subProp(this.store.select('events', 'labelMaps', 'task_labels'), 'task_labels')
  }

  saveTable() {
    saveTable(this.saveType, this.saveName, this.dialog, this.nestedTable)
  }

  async deleteTasks(toDelete: TaskTableData[], event) {
    const data: ConfirmDeleteData = {
      title: `Delete ${toDelete.length} task${toDelete.length === 1 ? '' : 's'}`,
      rect: event.target.getBoundingClientRect(),
      para: `Are you sure you want to delete ${toDelete.length > 1 ? `all ${toDelete.length} selected tasks` : 'the selected task'}?`,
      dialog: this.dialog
    }
    const del = await openConfirmDeleteGetResult(data)
    if (!del) return
    toDelete.forEach(({ fullTask: task }) => this.taskService.deleteTask(task))
  }

  selectIds(ids: Set<string>) {
    this.selectedTaskIds = Array.from(ids)
    // console.log(this.selectedTaskIds)
    setTimeout(() => this.buildMenu(false), 0)
  }

  private get selectedTasks(): Task[] {
    return (this.selectedTaskIds || []).map(id => this.tableRowsById[id].fullTask)
  }

  buildMenu(disableMenuTemporarily = true) {
    const tasks = this.selectedTasks
    const nTasks = tasks.length

    if (disableMenuTemporarily) this.disableMenuTemporarily()
    if (nTasks === 0) return

    const nFlagged = tasks.filter(t => t.flag).length
    this.menuItems = [{ title: `Set status`, method: this.setStatus.bind(this) }]

    if (nFlagged < nTasks) this.menuItems.push({ title: 'Flag', method: this.flag.bind(this) })
    if (nFlagged) this.menuItems.push({ title: 'Unflag', method: this.unflag.bind(this) })

    this.menuItems.push({ title: 'Set due date', method: this.setDueDate.bind(this) })

    if (this.filterPage === 'allTasks') {
      this.menuItems.push({ title: 'Delete', method: this.deleteTasks.bind(this) })
      return 
    }

    this.menuItems = [...this.menuItems, ...this.singleEventMenuItems ]
    this.menuItems.push({ title: 'Add labels', method: this.addLabels.bind(this) })
    if (tasks.some(t => t.labels && t.labels.length)) this.menuItems.push({ title: 'Remove labels', method: this.removeLabels.bind(this) })

    this.menuItems.push({ title: 'Delete', method: this.deleteTasks.bind(this) })    
  }

  public async updateLabelTemplate(labels: Label[]) {
    labels.forEach(label => this.task_labels[label.id] = label) // need to overwrite the old values here to both maintain order and propagate changes without losing filtered out template labels
    await this.eventService.updateEvent(this.eventService.eventId, { task_labels: Object.values(this.task_labels) })
  }

  public async addLabelsUpdateFunction({ labels, labelTemplate, labelsToggled, labelsChanged }: LabelUpdateFunctionParams) {
    if (labelsChanged) this.updateLabelTemplate(labelTemplate)

    if (!labelsToggled) return
    const tasks = this.selectedTasks.filter(task => !task.labels || labels.some(label => !task.labels.includes(label)))
    await Promise.all(tasks.map(task => this.taskService.updateTask(task.id, { labels: listUnion(task.labels, labels) }, { file: 'task-table.component.ts', function: 'addLabelsUpdateFunction', error: new Error })))
  }

  public async removeLabelsUpdateFunction({ labels, labelTemplate, labelsToggled, labelsChanged }: LabelUpdateFunctionParams) {
    if (labelsChanged) this.updateLabelTemplate(labelTemplate)

    if (!labelsToggled) return
    const filteredLabels = labelTemplate.map(l => l.id).filter(id => !labels.includes(id))
    if (filteredLabels.length === 0) return
    labels = filteredLabels
    const tasks = this.selectedTasks.filter(task => task.labels && task.labels.some(label => labels.includes(label)))
    await Promise.all(tasks.map(task => this.taskService.updateTask(task.id, { labels: listDifference(task.labels, labels) }, { file: 'task-table.component.ts', function: 'removeLabelsUpdateFunction', error: new Error })))
  }

  async removeLabels(rows: TaskTableData[], event) { 
    removeLabelsFromMany(this.removeLabelsUpdateFunction.bind(this), this.task_labels, this.dialog, 'task', this.selectedTasks, event)
  }

  async addLabels(rows: TaskTableData[], event) { 
    addLabelsToMany(this.addLabelsUpdateFunction.bind(this), this.task_labels, this.dialog, 'task', this.selectedTasks, event)
  }


  async manageVisibility(rows: TaskTableData[], event) { 
    const peopleDestroyer: Subject<boolean> = new Subject()
    const mandatoryEntry = { people: {}, groups: {} }
    const originalSelected = { people: {}, groups: {} }
    let finalSelected
    const data: PeoplePickerData = {
      x: event.x - 300,
      y: event.y + 30,
      people: this.workforce,
      groups: this.wf_groups,
      multi: true,
      selected: jsonCopyObject(originalSelected),
      emitter: this.peopleEmitter,
      mandatoryEntry,
      title: 'Visibility',
    }

    this.peopleEmitter.pipe(takeUntil(peopleDestroyer)).subscribe(selected => finalSelected = selected)
    await openDialogGetResult(this.dialog, PeoplePickerComponent, {
      width: '300px',
      autoFocus: false,
      backdropClass: 'clear',
      panelClass: ['popup-center'],
      data
    })
    
    peopleDestroyer.next(true)
    peopleDestroyer.complete()
    if (!finalSelected) {
      return
    }
    // console.log({ finalSelected })
    deleteFalseEntriesInPlace(finalSelected['people'])
    deleteFalseEntriesInPlace(finalSelected['groups'])
    if (JSON.stringify(originalSelected) === JSON.stringify(finalSelected)) {
      return
    }
    const groups = Object.keys(finalSelected.groups)
    const people = Object.keys(finalSelected.people)
    if (!groups.length && !people.length) {
      return console.log("No groups or people selected, returning...")
    }
    let updated = false
    this.selectedTasks.forEach(task => {
      const creator = this.workersByUId[task.createdBy]
      const creatorEmail = creator ? creator.email : task.visibility[0]
      if (!creatorEmail || !creator) {
        return console.log("MISSING creator or creator email for task!  Not updating visibility", { task, workersByUId: this.workersByUId })
      }
      updated = true
      task.groupVisibility = groups
      task.visibility = people
      if (task.responsibility && !task.visibility.includes(task.responsibility)) {
        task.visibility.unshift(task.responsibility)
      }
      if (!task.visibility.includes(creatorEmail)) {
        task.visibility.unshift(creatorEmail)
      }
      this.taskService.updateTask(task.id, task)
    })
  }

  async assignPerson(rows: TaskTableData[], event) { 
    const workerEmail = await getSingleWorkforcePersonEmail(event, this.workforce, this.dialog)
    if (!workerEmail) return
    this.selectedTasks.forEach(task => {
      task.responsibility = workerEmail
      if (!task.visibility.includes(workerEmail)) {
        task.visibility.push(workerEmail)
      }
      this.taskService.updateTask(task.id, task)
    })
  }

  async setProgram(rows: TaskTableData[], event) {
    const NO_PROGRAM_TITLE = `(No ${this.uiService.programTerm.text})`
    const data: ButtonListData<Program> = {
      x: event.x - 125,
      y: event.y + 27,
      title: `Choose a ${this.uiService.programTerm.btn_lower}`,
      choiceObjects: [{ title: NO_PROGRAM_TITLE } as Program].concat(this.programService.programs),
      choiceKey: 'title',
      color: this.uiService.labelsByType.program.color,
      selected: null,
      selectedKey: 'id'
    }
    const { selected } = await openDialogGetResult<{ selected: string }>(this.dialog, ButtonListDialogComponent, {
      width: '250px',
      autoFocus: false,
      data,
      backdropClass: 'clear',
    })
    if (!selected) return
    const choice = data.choiceObjects.find(p => p.id === selected)
    if (choice.title === NO_PROGRAM_TITLE) {
      const tasks = this.selectedTasks.filter(task => task.program)
      tasks.forEach(task => this.taskService.updateTask(task.id, { program: null }))
    } else {
      const tasks = this.selectedTasks.filter(task => !task.program || task.program !== choice.id)
      tasks.forEach(task => this.taskService.updateTask(task.id, { program: choice.id }))
    }
  }

  disableMenuTemporarily() {
    this.menuDisabled = true
    setTimeout(() => this.menuDisabled = false, TASK_SAVE_DEBOUNCE_AND_SKIP_DUPLICATE_MS)
  }

  async setDueDate(rows: TaskTableData[], event) {
    const data = { date: new Date() }
    const result = await openDialogGetResult(this.dialog, ChangeScheduleDateDialogComponent, { width: '360px', disableClose: true, data })
    if (!result) return
    const dueDate = date2str(result['schedule_date'])
    if (!dueDate) return
    this.selectedTasks.forEach(t => this.taskService.updateTask(t.id, { dueDate }))  
  }

  flag(rows: TaskTableData[], event) {
    this.selectedTasks.filter(t => !t.flag).forEach(t => this.taskService.toggleTaskFlagged(t))
    this.buildMenu()
  }

  unflag(rows: TaskTableData[], event) {
    this.selectedTasks.filter(t => t.flag).forEach(t => this.taskService.toggleTaskFlagged(t))
    this.buildMenu()
  }

  async setStatus(rows: TaskTableData[], event) {
    const data: TaskStatusDialogData = {
      nTasks: this.selectedTasks.length
    }
    const status = await openDialogGetResult<TaskStatus>(this.dialog, TaskStatusDropdownDialogComponent, {
      width: '250px',
      autoFocus: false,
      data,
      backdropClass: 'clear',
    })
    console.log({ status })
    if (!status) return

    this.selectedTasks.forEach(task => this.taskService.updateStatus(task, status))
  
  }


  toggleSeen(row: TaskTableData) {
    this.taskService.toggleTaskSeen(row.fullTask)
  }

  toggleIcon(d: TableIconEmitterData<TaskTableData>) {
    const taskRow = d.row
    console.log({ taskRow })
    if (taskRow.readOnly) return // for all tasks that are from suspended accounts and the like
    switch (d.iconType) {
      case 'check':
        this.taskService.toggleTaskCompleted(taskRow.fullTask)
        break
      case 'flag':
        this.taskService.toggleTaskFlagged(taskRow.fullTask)
        break
      default:
        console.log(`Unhandled icon type: ${d.iconType}`)
        break
    }
    this.buildMenu()
  }

  async editTask(task: Task, eventId: string, focusTitle = true) {
    this.nestedTable.pause()
    if (this.otherChangeDetectorRef) this.otherChangeDetectorRef.detach()
    await this.taskViewService.manageTaskThroughDialog(this.dialog, task, eventId, focusTitle, this.changeDetectorRef, this.filterPage)
    if (this.otherChangeDetectorRef) this.otherChangeDetectorRef.reattach()
    this.nestedTable.resume()
  }

  async manageTask(taskRow: TaskTableData) {
    const task = taskRow.fullTask
    await this.editTask(task, taskRow.eventId, !task.name)
  }

  loadCondensed() {
    this.condensed = loadLocal(`${this.filterPage}:${toggleFilterTypes.condensed}`, false)
  }
}
